<template>
  <div class="container mt-4">
    <!-- Breadcrumb -->
    <b-breadcrumb class="mb-3 mt-2">
      <b-breadcrumb-item href="/">
        <i class="fa fa-home" aria-hidden="true"></i> Home
      </b-breadcrumb-item>
      <b-breadcrumb-item href="/residentslist-chart">Residents</b-breadcrumb-item>
      <b-breadcrumb-item active>{{ getBread("-chart") }}</b-breadcrumb-item>
    </b-breadcrumb>



    <div v-if="loading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="text-center text-danger">
      <p>Error loading data.</p>
    </div>

    <div v-else>
      <div class="d-flex flex-wrap gap-2 mb-4 align-items-end">

        <div v-if="getUserRole('carers') || getUserRole('owners')">
          <button class="btn btn-primary" @click="showAddModal = true">
            Add Charting Note
          </button>
        </div>
        <div v-if="getUserRole('owners')">
          <button class="btn btn-secondary" @click="downloadPDF">
            Download PDF
          </button>
        </div>
        <div class="ms-auto">
          <input type="month" id="monthPicker" class="form-control" v-model="selectedMonth" @change="onMonthChange" />
        </div>

        <div class="ms-auto">
          <select class="form-select" v-model="sortOrder" @change="onSortOrderChange">
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>
        </div>


      </div>


      <div class="customCard">
        <h1 class="text-center pb-3 fs-5 fs-md-5">
          List of Charting Notes {{ fullName }}
        </h1>
      </div>

      <div ref="pdfContent" style="margin-top: 0;">
        <div class="row">
          <div class="col-12 mb-3 page-break" v-for="charting in paginatedChartings" :key="charting.id">
            <div :style="{ paddingTop: topmargin + 'in' }">
              <div class="card shadow-sm">
                <div class="card-body">
                  <p class="card-text"> <strong>From:</strong> {{ formatDateTimeNormal(new
                    Date(charting.attributes.daterecordedfrom)) }}</p>
                  <p class="card-text"> <strong>To:</strong> {{ formatDateTimeNormal(new
                    Date(charting.attributes.daterecordedto)) }}</p>
                  <p class="card-text"><strong>Total Hours:</strong> {{ calculateTotalHours(charting.attributes.daterecordedfrom, charting.attributes.daterecordedto)  }}</p>
                  <p class="card-text"><strong>Note:</strong> {{ charting.attributes.chartingnote }}</p>
                  <p class="card-text"><strong>Type:</strong> {{ getNoteTypeText(charting.attributes.notetype) }}</p>
                  <p class="card-text"><strong>Taker:</strong> {{ charting.attributes?.users_permissions_user?.data?.attributes?.fullname?.trim()
      ?? charting.attributes?.taker?.trim() ?? "" }}</p>
                  <div
                    v-if="!isPrintingAll && (getUserRole('carers') || getUserRole('editors') || getUserRole('owners'))"
                    class="d-flex gap-2">
                    <a href="#" @click.prevent="editEntry(charting)" class="btn btn-link p-0" title="Edit">
                      <i class="fa fa-edit text-primary"></i>
                    </a>
                    <a href="#" @click.prevent="deleteEntry(charting)" class="btn btn-link p-0" title="Delete">
                      <i class="fa fa-trash text-danger"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center flex-wrap mt-3">
        <p>
          Showing {{ startIndex + 1 }} to {{ endIndex }} of
          {{ result.residentchartings.data.length }} entries
        </p>
        <ul class="pagination flex-wrap">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Prev</a>
          </li>
          <li class="page-item" v-for="page in totalPages" :key="page" :class="{ active: currentPage === page }">
            <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
          </li>
        </ul>
      </div>
    </div>


    <!-- Edit Modal -->
    <b-modal size="lg" v-model="showEditModal" hide-footer :title="'Edit Charting Note ' + fullName">
      <form v-if="currentCharting" @submit.prevent="updateEntry">


        <div class="mb-3">
          <label for="chartingnote" class="form-label">Charting Note</label>
          <textarea class="form-control" id="chartingnote" v-model="currentCharting.attributes.chartingnote"
            required></textarea>
        </div>

        <div class="mb-3">
          <label for="notetype" class="form-label">Note Type</label>
          <select class="form-select" id="notetype" v-model="currentCharting.attributes.notetype" required>
            <option v-for="option in noteTypes" :key="option.value" :value="option.value">
              {{ option.text }}
            </option>
          </select>
        </div>


        <div class="mb-3">
          <label for="daterecordedfrom" class="form-label">From</label>
          <input type="datetime-local" class="form-control" id="daterecordedfrom" v-model="formattedDateRecordedFrom"
            required />
        </div>

        <div class="mb-3">
          <label for="daterecordedto" class="form-label">To</label>
          <input type="datetime-local" class="form-control" id="daterecordedto" v-model="formattedDateRecordedTo"
            required />
        </div>

        <div class="d-grid">
          <button type="submit" class="btn btn-primary">Save changes</button>
        </div>
      </form>
      <div v-if="updateError" class="alert alert-danger mt-3">
        {{ updateError }}
      </div>
    </b-modal>

    <!-- Add Modal -->
    <b-modal v-model="showAddModal" hide-footer :title="'Add Charting Note ' + fullName">
      <form @submit.prevent="addEntry">
        <div class="mb-3">
          <label for="daterecordedfrom" class="form-label">From</label>
          <input type="datetime-local" class="form-control" id="daterecordedfrom" v-model="newCharting.daterecordedfrom"
            required />
        </div>
        <div class="mb-3">
          <label for="daterecordedto" class="form-label">To</label>
          <input type="datetime-local" class="form-control" id="daterecordedto" v-model="newCharting.daterecordedto"
            required />
        </div>
        <div class="mb-3">
          <label for="chartingnote" class="form-label">Charting Note</label>
          <textarea class="form-control" id="chartingnote" v-model="newCharting.chartingnote" required></textarea>
        </div>
        <div class="mb-3">
          <label for="notetype" class="form-label">Note Type</label>
          <select class="form-select" id="notetype" v-model="newCharting.notetype" required>
            <option v-for="option in noteTypes" :key="option.value" :value="option.value">
              {{ option.text }}
            </option>
          </select>
        </div>
        <div class="d-grid">
          <button type="submit" class="btn btn-primary">Add Charting Note</button>
        </div>
      </form>
      <div v-if="addError" class="alert alert-danger mt-3">
        {{ addError }}
      </div>
    </b-modal>
  </div>
</template>


<script>
import { useQuery, useMutation } from "@vue/apollo-composable";
import html2pdf from 'html2pdf.js'
import { GET_CHARTINGS_BY_RESIDENT, UPDATE_CHARTING, CREATE_CHARTING, GET_FULLNAME_BY_ID, DELETE_CHARTING } from "../hooks/queries";
import { ref, computed } from "vue";
import { useRoute } from 'vue-router'; // Import useRoute from Vue Router  
import { noteTypes, formatDateTime, formatDate, formatDateTimeNormal, getBread, getUserRole } from "../components/values";

export default {
  name: "App",

  setup() {

    const dnow = new Date();
    //const startDate = new Date(dnow.getFullYear(), dnow.getMonth(), 1).toISOString(); // first day of month
    //const endDate = new Date(dnow.getFullYear(), dnow.getMonth() + 1, 0, 23, 59, 59).toISOString(); // last day of month


    const { mutate: deleteResidentcharting } = useMutation(DELETE_CHARTING);

    const selectedMonth = ref(`${dnow.getFullYear()}-${String(dnow.getMonth() + 1).padStart(2, '0')}`);
    const sortOrder = ref("desc"); // default to newest first

    const onSortOrderChange = async () => {
      await refetch();
    };


    const startDate = computed(() => {
      const [year, month] = selectedMonth.value.split("-");
      return new Date(year, month - 1, 1).toISOString();
    });

    const endDate = computed(() => {
      const [year, month] = selectedMonth.value.split("-");
      return new Date(year, month, 0, 23, 59, 59).toISOString();
    });


    const route = useRoute(); // Use useRoute to get the current route
    const residentId = route.params.id; // Access the route parameter directly
   

    const { result, loading, error, refetch } = useQuery(
      GET_CHARTINGS_BY_RESIDENT,
      () => ({
        residentId: residentId,
        pagination: { start: 0, limit: 50 },
        startDate: startDate.value,
        endDate: endDate.value,
        sort: [`daterecordedfrom:${sortOrder.value}`], // <-- Sorting here
      }),
      {
        fetchPolicy: 'network-only',
      }
    );


    const { mutate: updateResidentcharting } = useMutation(UPDATE_CHARTING);
    const { mutate: createCharting } = useMutation(CREATE_CHARTING);
    const currentCharting = ref(null);
    const updateError = ref(null);
    const addError = ref(null);
    const showEditModal = ref(false);
    const showAddModal = ref(false);
    const now = new Date().toISOString().slice(0, 16); // Current date and time in ISO format without seconds

    const { result: resultfullname, loading: loadingfullname, error: errorfullname, refetch: refetchfullname } = useQuery(GET_FULLNAME_BY_ID, {
      id: residentId,
    });


    // Watch the result and compute the full name only when the data is available
    const fullName = computed(() => {
      const data = resultfullname.value?.resident?.data;
      if (data) {
        return `(${data.attributes.firstname} ${data.attributes.lastname})`;
      }
      return null;
    });


    const newCharting = ref({
      daterecordedfrom: now,
      daterecordedto: now,
      notetype: "",
      chartingnote: "",
      taker: "",
      publishedAt: new Date().toISOString()
    });

    const addEntry = async () => {
      addError.value = null;

      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      //console.log(user);

      try {
        await createCharting({
          data: {
            resident: residentId, // Include the residentId in the input data
            notetype: newCharting.value.notetype,
            daterecordedfrom: formatDateTime(newCharting.value.daterecordedfrom),
            daterecordedto: formatDateTime(newCharting.value.daterecordedto),
            chartingnote: newCharting.value.chartingnote,
            taker: user?.fullname || 'Unknown',
            users_permissions_user: user.id,
            publishedAt: new Date().toISOString()
          },
        });
        showAddModal.value = false;
        newCharting.value = {
          daterecordedfrom: now,
          daterecordedto: now,
          notetype: "",
          chartingnote: "",
        };
        await refetch();  // Refetch the list of chartings after adding a new one
        await refetchfullname();
      } catch (e) {
        //console.error("Error adding entry:", e);
        addError.value = `Failed to add charting note. Please try again.`;
      }
    };

    const editEntry = (charting) => {
      //currentCharting.value = { ...charting }; // Copy charting to avoid directly modifying the original data
      currentCharting.value = JSON.parse(JSON.stringify(charting)); // Create a deep copy of the docuement object
      showEditModal.value = true;
    };

    const updateEntry = async () => {
      updateError.value = null; // Reset the error message
      try {
        await updateResidentcharting({
          id: currentCharting.value.id,
          data: {
            notetype: currentCharting.value.attributes.notetype,
            daterecordedfrom: formatDateTime(currentCharting.value.attributes.daterecordedfrom),
            daterecordedto: formatDateTime(currentCharting.value.attributes.daterecordedto),
            chartingnote: currentCharting.value.attributes.chartingnote,
          },
        });
        //console.log("Updated entry:", currentCharting.value);
        showEditModal.value = false;
      } catch (error) {
        //console.error("Error updating entry:", error);
        updateError.value = "Failed to update Charting. Please try again.";
      }
    };

    const deleteEntry = async (charting) => {
      const confirmed = confirm("Are you sure you want to delete this charting note?");
      if (!confirmed) return;

      try {
        await deleteResidentcharting({
          id: charting.id,
        });

        await refetch(); // Refresh the list
      } catch (error) {
        console.error("Failed to delete entry:", error);
        alert("Error deleting charting note. Please try again.");
      }
    };


    const getNoteTypeText = (value) => {
      const option = noteTypes.find(option => option.value === value);
      return option ? option.text : '';
    };

    const onMonthChange = async () => {
      console.log("User picked month:", selectedMonth.value);
      console.log("Start Date:", startDate.value);
      console.log("End Date:", endDate.value);
      await refetch(); // re-fetch on change of selectedMonth
    };


    return {
      result,
      loading,
      error,
      sortOrder,
      onSortOrderChange,
      formatDate,
      showEditModal,
      showAddModal,
      currentCharting,
      newCharting,
      editEntry,
      updateEntry,
      deleteEntry,
      addEntry,
      updateError,
      addError,
      noteTypes,
      formatDateTimeNormal,
      getNoteTypeText,
      residentId,
      getBread,
      getUserRole,
      errorfullname,
      loadingfullname,
      fullName,
      selectedMonth,
      onMonthChange,
    };
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 4,
      isPrintingAll: false,
      topmargin: 0
    };
  },


  computed: {

    totalPages() {
      return Math.ceil(this.result.residentchartings.data.length / this.itemsPerPage);
    },
    paginatedChartings() {
      if (this.isPrintingAll) {
        return this.result.residentchartings.data;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.result.residentchartings.data.slice(start, start + this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(
        this.startIndex + this.itemsPerPage,
        this.result.residentchartings.data.length
      );
    },

    formattedDateRecordedFrom: {
      get() {
        return this.formatDateTimeEdit(this.currentCharting.attributes.daterecordedfrom);
      },
      set(newValue) {
        this.currentCharting.attributes.daterecordedfrom = newValue;
      }
    },
    formattedDateRecordedTo: {
      get() {
        return this.formatDateTimeEdit(this.currentCharting.attributes.daterecordedto);
      },
      set(newValue) {
        this.currentCharting.attributes.daterecordedto = newValue;
      }
    },
  },
  methods: {
    changePage(page) {
      if (page > 0 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    formatDateTimeEdit(dateTime) {
      // Create a Date object from the input date-time string
      let date = new Date(dateTime);

      // Format the date and time to 'YYYY-MM-DDTHH:MM'
      let formattedDate = date.toISOString().slice(0, 16);

      return formattedDate;
    },
  calculateTotalHours (fromDate, toDate) {
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diffInHours = (end - start) / (1000 * 60 * 60);    
    return Math.round(diffInHours);
  },

    async downloadPDF() {
      this.isPrintingAll = true;
      this.topmargin = 2;

      // Wait for full DOM update
      await this.$nextTick();

      const element = this.$refs.pdfContent;
      const resident = this.fullName;
      const options = {
        margin: 0.5,
        filename: `ChartingNotes-${this.fullName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      html2pdf().set(options).from(element)
        .toPdf()
        .get('pdf')
        .then(function (pdf) {
          const totalPages = pdf.internal.getNumberOfPages();

          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);

            // Add header text

            pdf.setTextColor(40);
            pdf.setFontSize(20);
            //pdf.text("Charting Notes  " + new Date().toLocaleDateString(), 0.5, 0.3);

            let pageWidth = pdf.internal.pageSize.getWidth();
            let text = "Melbourne Home Care Services";
            let textWidth = pdf.getTextWidth(text);
            let x = (pageWidth - textWidth) / 2;
            pdf.text(text, x, 0.5); // centered at y = 0.5


            pdf.setFontSize(12);
            text = "1701 N Collins Blvd, STE 140, Richardson, TX 75080";
            textWidth = pdf.getTextWidth(text);
            x = (pageWidth - textWidth) / 2;
            pdf.text(text, x, 0.8);


            text = "Phone: 972 231-2500: Email: info@melbournepersonalcare.com";
            textWidth = pdf.getTextWidth(text);
            x = (pageWidth - textWidth) / 2;
            pdf.text(text, x, 1.0);

            pdf.setFontSize(16);
            text = "Observation Notes";
            textWidth = pdf.getTextWidth(text);
            x = (pageWidth - textWidth) / 2;
            pdf.text(text, x, 1.4);

            pdf.setFontSize(14);
            text = "Resident: " + resident;
            textWidth = pdf.getTextWidth(text);
            x = (pageWidth - textWidth) / 2;
            pdf.text(text, x, 1.8);


            pdf.setFontSize(10);
            // Optional: Add page number
            pdf.text(`Page ${i} of ${totalPages}`, 7.0, 10.7); // bottom right (adjust if needed)

            // Optional: Add a logo (uncomment and use base64 data)
            // const imgData = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUg...'; 
            // pdf.addImage(imgData, 'PNG', 7, 0.1, 0.5, 0.5); // x, y, width, height
          }
        })
        .save()
        .then(() => {
          this.isPrintingAll = false;
          this.topmargin = 0;
        });
    }


  },

};
</script>

<style>
.container {
  max-width: 80%;
}

.d-grid {
  background-color: #e8e8e8;
  margin-bottom: 20px;
}

.text-center {
  text-align: center;
}

.spinner-border {
  margin-top: 20px;
}

.pagination {
  margin: 0;
}

.page-break {
  page-break-before: always;
  break-before: page;
}

.page-break:first-child {
  page-break-before: auto;
  /* Avoid blank first page */
}
</style>
